import { customFetch, STORAGE_KEYS } from '@agos/api-client'
import type { FranchiseSummary } from '@agos/api-client/model'
import { T, tt, useAuth } from '@agos/shared'
import { cn } from '@agos/ui'
import { Check, ChevronDown, Store } from 'lucide-react'
import { useEffect, useState } from 'react'


/**
 * Ported from legacy `components/franchise-switcher.tsx`.
 *
 * Visible to platform super-admins only. A regular employee's franchise is
 * fixed by the `franchiseId` claim in their token and cannot be changed from
 * the browser — BACKEND.md §5 makes that a server-side rule, and this component
 * simply never offers the choice.
 *
 * Switching reloads the page on purpose: TanStack Query caches would otherwise
 * carry one franchise's orders into another's screens.
 */
export function FranchiseSwitcher() {
  const { user } = useAuth()
  const [franchises, setFranchises] = useState<FranchiseSummary[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState(false)

  const isSuperAdmin = Boolean(user?.isSuperAdmin)

  useEffect(() => {
    if (!isSuperAdmin) return
    let cancelled = false

    void (async () => {
      try {
        const list = await customFetch<FranchiseSummary[]>('/api/v1/central/franchises')
        setFranchises(list)

        const stored = localStorage.getItem(STORAGE_KEYS.franchiseId)
        const storedId = stored ? Number(stored) : null
        if (storedId && Number.isFinite(storedId)) {
          setActiveId(storedId)
        } else if (list[0]?.id != null) {
          // `id` is optional on the generated type, because the schema declares no `required` list.
          // The registry always answers one; narrowing here says so rather than asserting it.
          localStorage.setItem(STORAGE_KEYS.franchiseId, String(list[0].id))
          setActiveId(list[0].id)
        }
      } catch {
        if (!cancelled) setFailed(true)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isSuperAdmin])

  if (!isSuperAdmin) return null

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border text-sm font-medium text-muted-foreground">
        <Store className="w-4 h-4" />
        <span className="hidden sm:inline">
          <T>Načítavam pobočky…</T>
        </span>
      </div>
    )
  }

  if (failed) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-destructive/10 rounded-lg border border-destructive/30 text-sm font-medium text-destructive">
        <Store className="w-4 h-4" />
        <span className="hidden sm:inline">
          <T>Chyba pobočiek</T>
        </span>
      </div>
    )
  }

  if (franchises.length === 0) return null

  const active = franchises.find((franchise) => franchise.id === activeId) ?? null

  const switchTo = (franchiseId: number) => {
    localStorage.setItem(STORAGE_KEYS.franchiseId, String(franchiseId))
    window.location.reload()
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((isOpen) => !isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg hover:bg-accent border border-border transition-colors text-sm font-medium text-foreground"
      >
        <Store className="w-4 h-4 text-primary" />
        <span className="truncate max-w-[180px] hidden sm:inline">
          {active ? active.name : tt('Vyberte pobočku')}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-12 right-0 w-64 bg-card border border-border rounded-xl shadow-xl z-50 py-2 overflow-hidden">
            <div className="px-3 pb-2 mb-2 border-b border-border/50">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <T>Pobočky</T>
              </h3>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {franchises.map((franchise) => (
                <button
                  key={franchise.id}
                  type="button"
                  onClick={() => franchise.id != null && switchTo(franchise.id)}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-2.5 hover:bg-accent text-sm transition-colors text-left',
                  )}
                >
                  <span className="font-medium text-foreground truncate">
                    <T>{franchise.name}</T>
                  </span>
                  {activeId === franchise.id && <Check className="w-4 h-4 text-primary shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
